import * as React from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { fetchData } from '../utils/fetchData'
import MessageBox from '../components/MessageBox'
import Button from '../components/Button'
import Page from '../components/layout/Page'
import Content from '../components/layout/Content'
import Modal from '../components/Modal'
import Input from '../components/Input'
import ImageCrop from '../components/ImageCrop'
import Tags from '../components/Tags'
import Arrow from '../icons/Arrow'
import LinkIcon from '../icons/Link'
import Trash from '../icons/Trash'
import TrashLarge from '../icons/TrashLarge'
import Loader from '../icons/Loader'
import ProtectedRoute from '../helpers/ProtectedRoute'
import { isValidField } from '../helpers/validation'
import { Image } from '../types/Image'
import { Media } from '../types/User'
import styles from '../styles/EditImage.module.css'
import Gdpr from '../components/Gdpr'
import { WarningIcon } from '../icons/Warning'
import { InitialMetaData, MetaData } from '../types/MetaData'
import { isValidDate } from '../utils/isValidDate'
import { isMetaDataNew } from '../utils/isMetaDataNew'
import { mapPersonkategori } from '../utils/mapPersonKategori'
import Banner from '../components/Banner'
import Checkbox from '../components/Checkbox'
import OptimizedImage from '../components/OptimizedImage'

function ImageInfo () {
  const STORAGE_KEY = 'media'
  const localStorageMedia = localStorage.getItem(STORAGE_KEY)
  let media: Media | null = null
  if (localStorageMedia) {
    try {
      media = JSON.parse(localStorageMedia)
    } catch (error) {
      console.log(error)
    }
  }
  const navigate = useNavigate()
  const token = media?.token ?? null
  const { storeId, channelType, id } = useParams()
  const [image, setImage] = React.useState<Image>()
  const [imageName, setImageName] = React.useState('')
  const [validImageName, setValidImageName] = React.useState('')
  const [tags, setTags] = React.useState<string[]>([])
  const [globalImage, setGlobalImage] = React.useState<boolean>()
  const [croppedImageUrl, setCroppedImageUrl] = React.useState('')
  const [croppedImageAspectRatio, setCroppedImageAspectRatio] = React.useState<number>(1)
  const [urlCopied, setUrlCopied] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [imageFormatModalOpen, setImageFormatModalOpen] = React.useState(false)
  const [imageUrlModalOpen, setImageUrlModalOpen] = React.useState(false)
  const [imageNameErrorText, setImageNameErrorText] = React.useState('')
  const [gdprErrorList, setGdprErrorList] = React.useState<{ errorType: string; errorText: string }[]>([])
  const [imageSaved, setImageSaved] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [deleteImageModal, setDeleteImageModal] = React.useState(false)
  const [under18ModalIsActive, setUnder18ModalIsActive] = React.useState<boolean>(false)
  const [under18Consent, setUnder18Consent] = React.useState<boolean>(false)
  const [gdpr, setGdpr] = React.useState<boolean>(false)
  const [metaData, setMetaData] = React.useState<MetaData>({
    avtalsId: [],
    fotoDatum: '',
    personkategori: [],
    raderingsDatum: '',
  })

  function onOpenUrlModal () {
    setImageUrlModalOpen(true)
    setImageFormatModalOpen(false)
  }

  React.useEffect(() => {
    async function getImage () {
      setLoading(true)
      try {
        const res = await fetchData(`/api/cloudinary/images/${id}`, {
          method: "GET",
        })

        if (res) {
          const { data, status } = res
          if (status === 200 && data && data?.asset_id) {
            setImage(data)
            setImageName(data?.context?.custom?.name)
            setTags(data?.tags?.filter((t: string) => t !== channelType))
            setGlobalImage(data?.tags?.includes('global'))
            setMetaData((prev) => ({
              ...prev, 
              avtalsId: data?.metadata?.AvtalsId?.split(';') || [],
              fotoDatum: data?.metadata?.FotoDatum || '',
              personkategori: data?.metadata?.Personkategori
                ? mapPersonkategori(data.metadata.Personkategori, true)
                : [],
              raderingsDatum: data?.metadata?.RaderingsDatum || '',
            }))
            setUnder18Consent(data?.metadata?.Personkategori?.includes('Minderarig') || false)
            setGdpr(data?.metadata?.AvtalsId && data?.metadata?.Personkategori.length > 0 || false)
          }
        }

      } catch (error) {
        console.log(error)
      }
      setLoading(false)
    }
    if (token && id) {
      getImage()
    }
  }, [token, id, channelType])

  React.useEffect(() => {
    const timer = setTimeout(() => setImageSaved(false), 5000)
    return () => { clearTimeout(timer) }
  }, [imageSaved])

  async function copyUrl () {
    await navigator.clipboard.writeText(croppedImageUrl)
    setUrlCopied(true)
  }

  React.useEffect(() => {
    const timer = setTimeout(() => setUrlCopied(false), 5000)
    return () => { clearTimeout(timer) }
  }, [urlCopied])

  function withoutSpecialChars () {
    const specialCharsFound = imageName.match(/[^A-Za-z0-9åäöÅÄÖ\s?]/g)
    if (specialCharsFound) {
      setImageNameErrorText(`Bildnamnet får inte innehålla några specialtecken ( ${specialCharsFound.join(' ')} )`)
      return false
    }
    return true
  }

  function arrayToString(arr: string[]) {
    const filteredArray = arr.filter(id => id !== '')
    if (filteredArray.length > 1) {
      return filteredArray.join(';')
    }
    return filteredArray[0] || ''
  }

  function validateStep () {
    const gdprErrors: { errorType: string; errorText: string }[] = []

    const isValidImageName = isValidField(imageName, setValidImageName) && withoutSpecialChars()

    if (isValidImageName !== true) {
      setValidImageName('ERROR')
      !imageNameErrorText && setImageNameErrorText('Bildnamn saknas')
    }

    if (gdpr) {
      if (metaData.personkategori.length < 1 || (metaData.personkategori.includes('Minderårig') && metaData.personkategori.length < 2)) {
        gdprErrors.push({ errorType: 'personType', errorText: 'Välj typ av person i bilden' })
      }
  
      if (metaData.avtalsId.length < 1) {
        gdprErrors.push({ errorType: 'agreementIds', errorText: 'Fyll i referens / Avtals ID' })
      }

      if (metaData.personkategori.includes('Praktikant') || metaData.personkategori.includes('Privatperson')) {
        const photoDateRegex = /^\d{4}-\d{2}-\d{2}$/ // Regex for ÅÅÅÅ-MM-DD format

        if (!metaData.fotoDatum.trim()) {
          gdprErrors.push({ errorType: 'photoDate', errorText: 'Fyll i fotodatum' })
        } else if (!photoDateRegex.test(metaData.fotoDatum.trim()) || !isValidDate(metaData.fotoDatum.trim())) {
          gdprErrors.push({ errorType: 'photoDate', errorText: 'Fel format, ange datum enligt ÅÅÅÅ-MM-DD' })
        }
        if (!metaData.raderingsDatum.trim()) {
          gdprErrors.push({ errorType: 'photoDeletionDate', errorText: 'Fyll i raderingsdatum' })
        } else if (!photoDateRegex.test(metaData.raderingsDatum.trim()) || !isValidDate(metaData.raderingsDatum.trim())) {
          gdprErrors.push({ errorType: 'photoDeletionDate', errorText: 'Fel format, ange datum enligt ÅÅÅÅ-MM-DD' })
        }
      }
      setGdprErrorList(gdprErrors)

      if (gdprErrors.length === 0 && metaData.personkategori.includes('Minderårig') && !under18Consent) {
        setUnder18ModalIsActive(true)
        return false
      }
    }
    return gdprErrors.length === 0 && isValidImageName === true
  }

  async function onSave () {
    if (validateStep()) {
      if (image) {
        const currentTags = tags ? [channelType, ...tags].sort() : [channelType]
        const tagsAreNew = JSON.stringify(currentTags) !== JSON.stringify(image?.tags)

        const updatedMetaData: InitialMetaData = {
          ...metaData,
          avtalsId: arrayToString(metaData.avtalsId),
          personkategori: mapPersonkategori(metaData.personkategori, false),
        }  
        const metaDataIsNew = image.metadata ? isMetaDataNew(updatedMetaData, image?.metadata) : gdpr
        
        if (image.context?.custom?.name !== imageName || tagsAreNew || metaDataIsNew) {
          setSaving(true)
          try {
            const body: {
              publicId: string
              imageName: string
              tags: (string | undefined)[]
              metadata?: InitialMetaData
            } = {
              publicId: image?.public_id,
              imageName,
              tags: currentTags,
            }

            if (gdpr) {
              body.metadata = updatedMetaData
            }

            const res = await fetchData(`/api/cloudinary/image/update`, {
              method: "PUT",
              body,
            })
            if (res?.status === 200) {
              setImageSaved(true)
            }
          } catch (error) {
            console.log(error)
          }
          setSaving(false)
        }
      }
    }
  }

  async function onDeleteImage () {
    setDeleteImageModal(false)
    try {
      const res = await fetchData(`/api/cloudinary/image/delete`, {
        method: "DELETE",
        body: { publicId: image?.public_id },
      })

      if (res?.status === 200) {
        setTimeout(() => {
          navigate(`/${storeId}/${channelType}`)
        }, 2000)
      }
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <ProtectedRoute user={token}>
      <Page>
        <div className={styles.imageDetail}>
          <Content>
            <Link
              className={styles.backButton}
              to={`/${storeId}/${channelType}`}
            >
              <Arrow className={styles.arrowIcon} />
              <span>Till bildgalleriet</span>
            </Link>
            <h1 className={styles.heading}>Redigera</h1>
            {loading ? (
              <div>Laddar…</div>
            ) : image ? (
              <div className={styles.surface}>
                <div className={styles.cover}>
                  <OptimizedImage 
                    className={styles.image}
                    src={image?.secure_url}
                    aspectRatio={image?.width / image?.height}
                    sizes='(min-width: 66.5em) 33vw, 50vw'
                  />
                  <div className={styles.buttonsContainer}> 
                    {!globalImage && <button className={styles.deleteButton} onClick={() => setDeleteImageModal(true)}>
                      <Trash color='var(--redMid)' className={styles.deleteIcon} />
                      <span className={styles.deleteText}>Ta bort bild</span>
                    </button>}
                    <Button size='medium' color='redMid' className={styles.formatAndUrlButton} onClick={() => setImageFormatModalOpen(true)}>
                      <>
                        <LinkIcon className={styles.arrowIcon} />
                        Välj format och hämta URL
                      </>
                    </Button>
                  </div>
                </div>
                <div className={styles.info}>
                  <>
                    <div className={styles.inputContainer}>
                      <h3 className={styles.modalHeading}>Namn*</h3>
                      <Input
                        id="imageName"
                        type="text"
                        placeholder="Ex. “Smörgåstårta med räkor”"
                        value={imageName}
                        onChange={(e) => { setImageName(e.target.value); setValidImageName('IDLE') }}
                        error={validImageName === 'ERROR'}
                        errorText={imageNameErrorText}
                        errorIcon={validImageName === 'ERROR' && <WarningIcon color={'var(--redMid)'} className={styles.errorIcon} />}
                      />
                      <label className={styles.label} htmlFor="imageName">
                        Ge bilden ett beskrivande namn
                      </label>
                    </div>
                    <div className={styles.inputContainer}>
                      <h3 className={styles.modalHeading} style={{marginBottom: tags?.length > 0 ? '0.5rem' : '0'}}>
                        Tagg
                      </h3>
                      <Tags tags={tags} setTags={setTags} />
                    </div>
                    <Gdpr 
                      gdpr={gdpr} 
                      setGdpr={setGdpr}
                      gdprData={metaData}
                      setGdprData={setMetaData}
                      gdprErrorList={gdprErrorList}
                      setGdprErrorList={setGdprErrorList}
                    /> 
                    {imageSaved && <span className={styles.messageBox}><MessageBox type='noIcon' withTitle={false} message='Bildinformation sparad' /></span>}
                    <div className={styles.uploadAction}>
                      <Button className={styles.uploadActionButton} size='large' color="redMid" onClick={onSave}>
                        {saving ? (
                          <Loader className={styles.loader} />
                        ) : (
                          <span>Spara</span>
                        )}
                      </Button>
                    </div>

                  </>
                </div>
              </div>
            ) : (
              <div>Kunde inte hämta bild</div>
            )}

            <Modal
              onCancel={() => { setDeleteImageModal(false) }}
              active={deleteImageModal}
              maxWidth="48rem"
              title="Ta bort bild"
            >
              <div className={styles.deleteContainer}>
                <TrashLarge />
                <p className={styles.controlDeleteText}>Är du säker på att du vill ta bort bild? <br />
                  Bilden och all bildinformation kommer att raderas.</p>
                <div className={styles.buttonGroup}>
                  <Button size='large' color='pink' onClick={() => setDeleteImageModal(false)}>Nej, ångra</Button>
                  <Button size='large' color='red' onClick={() => onDeleteImage()}>Ja, ta bort bild</Button>
                </div>
              </div>
            </Modal>

            <Modal
              onCancel={() => setImageFormatModalOpen(false)}
              active={imageFormatModalOpen}
              maxWidth="78rem"
              title="Välj det bildformat bilden ska visas i"
              height="100%"
            >
              <ImageCrop
                imgSrc={image?.secure_url}
                imgHeight={image?.height}
                imgWidth={image?.width}
                imageName={imageName ? imageName : image?.context?.custom?.name ? image.context.custom.name : ''}
                imageUrl={image?.secure_url}
                onOpenUrlModal={onOpenUrlModal}
                setCroppedImageUrl={setCroppedImageUrl}
                setCroppedImageAspectRatio={setCroppedImageAspectRatio}
              />
            </Modal>

            <Modal
              onCancel={() => setImageUrlModalOpen(false)}
              active={imageUrlModalOpen}
              maxWidth="78rem"
              title="Kopiera URL:en för visning av valt bildformat"
            >
              {croppedImageUrl &&
                <div className={styles.croppedImageContainer}>
                  <OptimizedImage
                    className={styles.croppedImage}
                    src={croppedImageUrl}
                    sizes='(min-width: 64em) 33vw, 50vw'
                    aspectRatio={croppedImageAspectRatio}
                  />
                  <div className={styles.urlContainer}>
                    <p className={styles.boldText}>Bildens URL</p>
                    <div className={styles.urlTextBox}>
                      <input
                        id="imageUrl"
                        className={styles.urlInput}
                        type="text"
                        value={croppedImageUrl}
                        readOnly
                      />
                      <Button className={styles.urlButton} size='medium' onClick={copyUrl}>Kopiera URL</Button>
                    </div>
                  </div>
                  {urlCopied && <span className={styles.messageBox}><MessageBox type='noIcon' withTitle={false} message='URL kopierad' /></span>}
                </div>
              }
            </Modal>
            <Modal
              onCancel={() => setUnder18ModalIsActive(false)}
              active={under18ModalIsActive}
              maxWidth="32rem"
              title="Observera"
              modalBodyPadding="7rem 3rem 3rem"
              height="100%"
            >
              <div className={styles.consentModalContent}>
                <Banner theme="info">
                  <div className={styles.banner}>
                    <p className={styles.bannerText}>
                      Skriftligt samtycke från vårdnadshavare krävs vid material på barn. 
                      Observera att uppladdning av bilder med identifierbara personer innebär behandling av personuppgifter 
                      och att det är ditt ansvar att följa dataskyddsförordningens regler. <a href="https://www.ica.se/dataskydd" className={styles.bannerLink} target="_blank" rel="noreferrer">Besök www.ica.se/dataskydd</a> för mer information.
                    </p>
                  </div>
                </Banner>
                <Checkbox 
                  label="Jag förstår att jag är ansvarig" 
                  id="under18Consent" 
                  checked={under18Consent}
                  onChange={(e) => setUnder18Consent(e.target.checked)} 
                />
                <div className={styles.consentButtonContainer}>
                  <Button disabled={!under18Consent} size="medium" onClick={() => {setUnder18ModalIsActive(false); onSave()}}>
                    Spara
                  </Button>
                </div>
              </div>
            </Modal>
          </Content>
        </div>
      </Page>
    </ProtectedRoute>
  )
}

export default ImageInfo